import React, { ChangeEvent } from 'react'
import { CardElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js'
import { Else, If, Then, When } from 'react-if'
import { Button } from '@/atoms/Button'
import { ArrowLeftIcon } from '@/atoms/Icons/ArrowLeftIcon'
import { Input } from '@/atoms/Input'
import { TitleSM } from '@/atoms/Text'
import { PayPalPayment } from '@/molecules/PayPalPayment'
import { PaymentInfo } from '@/services/PifCheckoutService'
import { Offer } from '@/services/PifService'
import { isSupportedPayPalCurrency, isSupportedStripeCurrency } from '@/services/PifService/PifService'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { getDecimalsForCurrency } from '@/services/PriceFormatter/PriceFormatter'
import { isValidEmail, isValidName } from '@/utils/StripeFormValidator'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { PrefilledCreditCardInput } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/PrefilledCreditCardInput'
import { useAppleGooglePay } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/useAppleGooglePay'
import { usePayItForwardForm } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm'

export interface TheatricalPifExpressCheckoutStripeFormProps {
  offer: Offer
  isSubscription: boolean
  projectSlug: string
  isGuest: boolean
  clientSecret: string
  shouldOnlyShowCreditCardForm?: boolean
  setShouldOnlyShowCreditCardForm?: React.Dispatch<React.SetStateAction<boolean>>
  disabled?: boolean
}

export const TheatricalPifExpressCheckoutStripeForm: React.FC<TheatricalPifExpressCheckoutStripeFormProps> = ({
  isGuest = false,
  offer,
  isSubscription,
  projectSlug,
  clientSecret,
  shouldOnlyShowCreditCardForm = false,
  setShouldOnlyShowCreditCardForm,
  disabled = false,
}) => {
  const {
    name,
    setName,
    email,
    setEmail,
    errorMessage,
    isSubmitting,
    savedPaymentInfo,
    setSavedPaymentInfo,
    checkout,
    onPayItForwardComplete,
  } = usePayItForwardForm()
  const {
    isSubmittingWithWallet,
    errorMessageWithWallet,
    paymentRequest: walletPaymentRequest,
  } = useAppleGooglePay({
    onComplete: onPayItForwardComplete,
    offer,
    isGuest,
    clientSecret,
  })

  const { t } = useTranslate('pif')
  const nameFieldLabel = t('nameOnCard', 'Name on card')
  const emailFieldLabel = t('emailAddress', 'Email')
  const amount = formatPrice(offer.price, { currency: offer.currency })
  const amountToPayMonthly = t('amountToPayMonthly', 'Pay {{amount}} Monthly', {
    amount,
  })
  const amountToPayOneTime = t('amountToPayOneTime', 'Pay {{amount}}', {
    amount,
  })

  const handleToggleStripeFormClose = () => {
    if (setShouldOnlyShowCreditCardForm) setShouldOnlyShowCreditCardForm(!shouldOnlyShowCreditCardForm)
  }
  return (
    <form onSubmit={checkout}>
      <When condition={isSupportedPayPalCurrency(offer.currency)}>
        {!shouldOnlyShowCreditCardForm && (
          <PayPalPayment
            disabled={disabled}
            name={projectSlug}
            onComplete={onPayItForwardComplete}
            description={`Angel Studios: ${projectSlug}:pif`}
            softDescriptor={`${projectSlug || 'Pay it Forward'}`}
            sku={offer?.slug || name}
            currency={offer.currency.toUpperCase()}
            amount={getPriceForCurrency(offer.price, offer.currency).toFixed(getDecimalsForCurrency(offer.currency))}
            buttonHeight={50}
            isGuest={isGuest}
          />
        )}
      </When>
      {walletPaymentRequest && !shouldOnlyShowCreditCardForm && (
        <>
          <div className="h-50 mb-[7px] mt-1 rounded">
            <When condition={isSupportedStripeCurrency(offer.currency)}>
              <PaymentRequestButtonElement
                options={{
                  paymentRequest: walletPaymentRequest,
                  style: { paymentRequestButton: { height: '50px' } },
                }}
              />
            </When>
          </div>
        </>
      )}
      {!shouldOnlyShowCreditCardForm && (
        <Button
          disabled={disabled}
          onClick={handleToggleStripeFormClose}
          className="h-[50px] w-full rounded"
          variant="black"
        >
          {t('creditOrDebitCard', 'Credit or Debit Card')}
        </Button>
      )}

      <When condition={isSupportedStripeCurrency(offer.currency) && shouldOnlyShowCreditCardForm}>
        <div className="relative">
          <div
            onClick={handleToggleStripeFormClose}
            className="absolute left-0 top-0 flex w-fit cursor-pointer flex-row justify-end rounded-full bg-core-gray-100 p-1.5"
          >
            <ArrowLeftIcon color="core-gray-700" size={18} />
          </div>
          <TitleSM className="text-center" weight="bold" color="core-gray-950">
            {t('creditOrDebitCard', 'Credit or Debit Card')}
          </TitleSM>
        </div>
        <div className="mt-6">
          <Input
            required
            usePlaceholderAsLabel
            value={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            label={nameFieldLabel}
            isValid={isValidName(name)}
            inputClasses="border-core-gray-300 w-full !duration-[0ms] focus-visible:ring-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent focus-visible:border-oxide focus-visible:border-b-2"
            className="photon-paragraph-md mt-1 rounded bg-core-gray-100 px-2 py-2.5 text-gray-950"
            labelClassName={'photon-caption-sm text-gray-900 -mb-9 pt-9'}
            containerClassName={''}
          />
        </div>
        <div>
          <Input
            required
            usePlaceholderAsLabel
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            label={emailFieldLabel}
            isValid={isValidEmail(email)}
            inputClasses="border-core-gray-300 w-full !duration-[0ms] focus-visible:ring-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent focus-visible:border-oxide focus-visible:border-b-2"
            className="photon-paragraph-md mt-1 rounded bg-core-gray-100 px-2 py-2.5 text-gray-950"
            labelClassName={'photon-caption-sm text-gray-900 -mb-9 pt-9'}
          />
        </div>
        <div className="w-full py-2">
          <If condition={!!savedPaymentInfo}>
            <Then>
              <div className="photon-caption-sm text-sm text-gray-900">Card</div>
              <PrefilledCreditCardInput
                savedPayment={savedPaymentInfo as PaymentInfo}
                changeCard={() => setSavedPaymentInfo(undefined)}
              />
            </Then>
            <Else>
              <div className="photon-caption-sm text-sm text-gray-900">Card</div>
              <div className="mt-1 h-12 rounded bg-core-gray-100 px-2 py-3">
                <CardElement options={options} />
              </div>
            </Else>
          </If>
        </div>
        <Button
          aria-label="pay-button"
          variant="oxide-core"
          className="mt-4 flex w-full justify-center rounded-md p-4"
          disabled={disabled || isSubmitting || isSubmittingWithWallet}
        >
          <If condition={isSubmitting || isSubmittingWithWallet}>
            <Then>
              <div className="text-white">
                <Translate t={t} i18nKey="processingCheckout">
                  Processing
                </Translate>
                ...
              </div>
            </Then>
            <Else>{isSubscription ? amountToPayMonthly : amountToPayOneTime}</Else>
          </If>
        </Button>
      </When>
      <div className="-mb-4 pt-4 text-center text-red">
        {errorMessage ? errorMessage : errorMessageWithWallet ? errorMessageWithWallet : ''}
      </div>
    </form>
  )
}

const options = {
  style: {
    base: {
      color: '#141414',
      colorText: '#7B7979',
      backgroundColor: '#F5F5F4',
      borderRadius: '8px',
      height: '48px',
      fontSize: '16px',
      fontFamily: 'inherit',
      lineHeight: '28px',
      '::placeholder': {
        color: '#7B7979',
        colorText: '#7B7979',
        fontSize: '16px',
        fontFamily: 'inherit',
      },
    },
  },
}
